/**
 * PERX3-REDESIGN round 5: the single-hue tonal series ramp.
 *
 * The mechanism is a CSS custom-property FALLBACK, not a runtime branch. `--is-series-N` is declared
 * in the light reading only (`tokens.ts`, `SERIES_LIGHT`), so
 *
 *   var(--is-series-3, var(--is-accent-cyan))
 *
 * resolves to the ramp stop under the flag and to the hue the consumer already drew without it. A
 * palette therefore keeps its flag-OFF value in the same expression that names its ramp position:
 * there is nothing to branch on, nothing to thread through a component, and the ~30 palettes in the
 * suite — which disagree about their OFF order, so one token per position could never have
 * reproduced all of them — each carry their own fallback.
 */

const SERIES_STOPS = 8;

const stopOf = (index: number): number =>
  (((index % SERIES_STOPS) + SERIES_STOPS) % SERIES_STOPS) + 1;

/**
 * One ramp stop, falling back to `legacy`. `stop` is 1-based and wraps past eight — the ramp is the
 * only palette under the flag, so a ninth band repeats rather than reaching outside it.
 */
export const seriesStop = (stop: number, legacy: string): string =>
  `var(--is-series-${stopOf(stop - 1)}, ${legacy})`;

/**
 * A whole palette, mapped BY POSITION. The array that comes in is the one the consumer drew with the
 * flag off and stays the fallback of each entry, so the ORDER of the categories is what carries the
 * ramp: pass them healthiest-first and the navy lands on the healthiest band.
 */
export const seriesRamp = (legacy: readonly string[]): string[] =>
  legacy.map((hue, index) => seriesStop(index + 1, hue));

/**
 * The label ink a FILLED stop carries, falling back to `legacy`. Stops 1-3 are dark enough for white
 * and 4-6 and 8 are not; stop 7 is the dark neutral, so it takes white too. The split is held in the
 * `--is-on-series-*` tokens rather than computed here, so a hue change moves both at once.
 */
export const seriesInk = (stop: number, legacy: string): string =>
  `var(--is-on-series-${stopOf(stop - 1)}, ${legacy})`;

/** Every ink the ramp can put on a fill, for a consumer that inks a whole palette by position. */
export const seriesInks = (legacy: readonly string[]): string[] =>
  legacy.map((ink, index) => seriesInk(index + 1, ink));

/**
 * A palette whose LAST entry is the "other / unknown / unclassified" bucket. It names no position on
 * the scale, so it takes the ramp's neutral stop 8 rather than the next tone down — otherwise the
 * bucket that means "we do not know" would read as the lowest band on the scale.
 */
export const seriesRampWithOther = (legacy: readonly string[]): string[] => [
  ...seriesRamp(legacy.slice(0, -1)),
  seriesStop(8, legacy[legacy.length - 1])
];

/**
 * The hue a ramp expression falls back to — the colour the consumer drew with the flag OFF.
 *
 * A component that BRANCHES on the fill it was handed (the one case is
 * `SegmentedDistributionBar`, which refuses to paint a caption on two hues that clear no contrast
 * ratio) reads a `var(--is-series-N, <hue>)` string, not the hue, once its palette is ramped. It has
 * to compare the fallback or the flag-OFF render changes, which is the one thing round 5 may not do.
 */
export const legacyHueOf = (color: string): string => {
  const fallback = /^var\(--[a-z0-9-]+,\s*(.+)\)$/i.exec(color.trim());

  return fallback ? fallback[1].trim() : color;
};

/**
 * The label ink for a fill that may be a ramp stop: `var(--is-series-3, …)` inks with
 * `var(--is-on-series-3, legacy)`, and anything else keeps `legacy`. Lets a consumer that paints a
 * caption ON a series fill follow the ramp's contrast split without knowing which stop it drew.
 */
export const seriesInkFor = (fill: string, legacy: string): string => {
  const stop = /^var\(--is-series-(\d),/.exec(fill.trim());

  return stop ? `var(--is-on-series-${stop[1]}, ${legacy})` : legacy;
};
